import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { loadStrings } from '../config/loadStrings';

const StringsContext = createContext(null);

// Safe defaults if remote file is unreachable
const DEFAULT_STRINGS = {
  agent_name: 'SakCare Agent',
  brand: { appName: 'SakCare Agent', botName: 'SakCare Agent', logoSrc: '/bot-avatar.png' },
};

// Replace "{AGENT}" tokens anywhere in the JSON
const replaceTokens = (v, agent) => (typeof v === 'string' ? v.replaceAll('{AGENT}', agent) : v);
const withTokensDeep = (v, agent) => {
  if (Array.isArray(v)) return v.map((x) => withTokensDeep(x, agent));
  if (v && typeof v === 'object') {
    return Object.fromEntries(Object.entries(v).map(([k, val]) => [k, withTokensDeep(val, agent)]));
  }
  return replaceTokens(v, agent);
};

export function StringsProvider({ children }) {
  const [raw, setRaw] = useState(null);
  const [error, setError] = useState(null);


// Step 1: get base agent from raw JSON

  const baseAgent =
  raw?.agent_name || raw?.brand?.botName || raw?.brand?.appName || DEFAULT_STRINGS.agent_name;

   // Pre-apply {AGENT} tokens across the whole object
  const strings = useMemo(() => withTokensDeep(raw || DEFAULT_STRINGS, baseAgent), [raw]);


// Step 3: derive final agentName AFTER replacement
const agentName =
  strings?.agent_name || strings?.brand?.botName || strings?.brand?.appName || baseAgent;
 

  useEffect(() => {
    (async () => {
      try {
        const data = await loadStrings();
        setRaw(data);
      } catch (e) {
        setError(e);
        setRaw(null);
      }
    })();
  }, []);

  const refresh = async () => {
    try {
      const data = await loadStrings();
      setRaw(data);
    } catch (e) {
      setError(e);
    }
  };

  const value = useMemo(() => ({ strings, agentName, error, refresh }), [strings, agentName, error]);
  return <StringsContext.Provider value={value}>{children}</StringsContext.Provider>;
}

export function useStrings() {
  const ctx = useContext(StringsContext);
  if (!ctx) throw new Error('useStrings must be used within <StringsProvider>');
  return ctx;
}
